import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import SiteNav from "../../components/SiteNav";
import "./orderkit.css";

const apiUrl = process.env.REACT_APP_API_URL;

const OrderSuccess: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const sessionId = searchParams.get("session_id");
  const teamId    = searchParams.get("team_id");

  const [status, setStatus]     = useState<"polling" | "success" | "error">("polling");
  const [order, setOrder]       = useState<any>(null);
  const [errorMsg, setErrorMsg] = useState<string>("");

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      setErrorMsg("Missing payment information. Please contact support.");
      return;
    }

    let attempts = 0;
    const MAX_ATTEMPTS = 15;

    const poll = async () => {
      try {
        const res = await fetch(`${apiUrl}/api/stripe/order-status`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ session_id: sessionId }),
        });

        if (res.ok) {
          const data = await res.json();
          setOrder(data);
          setStatus("success");
          return;
        }

        if (res.status === 404) {
          attempts++;
          if (attempts >= MAX_ATTEMPTS) {
            setStatus("error");
            setErrorMsg(
              "Your payment was received but order confirmation is taking longer than expected. " +
              "Please check your email or contact hello@tribelet.com."
            );
            return;
          }
          setTimeout(poll, 2000);
          return;
        }

        throw new Error("Unexpected error checking order status");
      } catch (err: any) {
        setStatus("error");
        setErrorMsg(err.message || "Something went wrong. Please contact support.");
      }
    };

    poll();
  }, [sessionId]);

  if (status === "polling") return (
    <div className="ok-page">
      <div className="grain-overlay" />
      <SiteNav />
      <div className="ok-content">
        <div className="ok-eyebrow">[ Processing ]</div>
        <h1 className="ok-title">Confirming your order.</h1>
        <p className="ok-subtitle">Please don't close this page — we're confirming your payment with Stripe.</p>
      </div>
    </div>
  );

  if (status === "error") return (
    <div className="ok-page">
      <div className="grain-overlay" />
      <SiteNav />
      <div className="ok-content">
        <div className="ok-eyebrow">[ Error ]</div>
        <h1 className="ok-title">Something went wrong.</h1>
        <p className="ok-subtitle">Your payment may have been received — please check your email before trying again.</p>
        <div className="ok-status-card ok-status-card--error">
          <p style={{ fontFamily: "'Fraunces', serif", marginBottom: '1rem' }}>{errorMsg}</p>
          {sessionId && (
            <div>
              <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.1em', color: 'var(--muted)' }}>
                Session ID
              </span>
              <div className="ok-session-id">{sessionId}</div>
            </div>
          )}
        </div>
        <button className="ok-checkout-btn" onClick={() => navigate("/", { state: { scrollTo: "tribes" } })}>
          Back to My Teams →
        </button>
      </div>
    </div>
  );

  return (
    <div className="ok-page">
      <div className="grain-overlay" />
      <SiteNav />
      <div className="ok-content">
        <div className="ok-eyebrow">[ Confirmed ]</div>
        <h1 className="ok-title">Order confirmed.</h1>
        <p className="ok-subtitle">Your custom kit is in production. Confirmation emails have been sent to you and our team.</p>

        <div className="ok-status-card">
          <div className="ok-spec-table">
            <div className="ok-spec-row">
              <span className="ok-spec-label">Order</span>
              <span className="ok-spec-value">#{order?.order_id}</span>
            </div>
            <div className="ok-spec-row">
              <span className="ok-spec-label">Total</span>
              <span className="ok-spec-value">£{parseFloat(order?.total || 0).toFixed(2)}</span>
            </div>
          </div>
        </div>

        <div className="ok-btn-stack">
          <button className="ok-checkout-btn" onClick={() => navigate("/orders")}>
            View My Orders →
          </button>
          {teamId && (
            <button className="ok-btn-ghost" onClick={() => navigate(`/team/${teamId}`)}>
              Back to Team Portal
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default OrderSuccess;
